import { cacheKeys, useCustomMutation, useGroups } from '@gripp/shared-logic';
import {
  CheckBoxSelector,
  Colors,
  DropDownView,
  DropdownFooter,
  SearchInput,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';

export type AddToGroupsModalProps = {
  routineIds: string[];
  workspaceId: string;
  anchorRef: React.RefObject<View>;
  isVisible: boolean;
  onDismiss: () => void;
};

const ASSIGN_ROUTINES_TO_GROUPS = `
  mutation AssignRoutinesToGroups($input: AssignRoutinesToGroupsInput!) {
    routine {
      assignRoutinesToGroups(input: $input) {
        success
        message
      }
    }
  }
`;

export const AddToGroupsModal = ({
  routineIds,
  workspaceId,
  anchorRef,
  isVisible,
  onDismiss,
}: AddToGroupsModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { groups, invalidateQuery: clearGroupsCache } = useGroups(workspaceId);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');

  const { mutateAsync: assignRoutinesToGroups } = useCustomMutation({
    query: ASSIGN_ROUTINES_TO_GROUPS,
  }) as any;

  const filteredGroups = useMemo(() => {
    if (!searchKeyword) return groups || [];
    return (groups || []).filter((group) =>
      group.name.toLowerCase().includes(searchKeyword.toLowerCase())
    );
  }, [groups, searchKeyword]);

  const isDirty = selectedGroups.length > 0;
  const canReset = () => selectedGroups.length > 0;

  const onGroupChange = (groupId: string) => {
    if (selectedGroups.includes(groupId)) {
      setSelectedGroups(selectedGroups.filter((id) => id !== groupId));
    } else {
      setSelectedGroups([...selectedGroups, groupId]);
    }
  };

  const handleSubmit = async () => {
    if (!isDirty) return;

    try {
      await assignRoutinesToGroups(
        {
          input: {
            routineIds,
            groupIds: selectedGroups,
            workspaceId,
          },
        },
        {
          onSettled: async () => {
            // Invalidate relevant caches
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: cacheKeys('routine').all,
              }),
              queryClient.invalidateQueries({
                queryKey: cacheKeys('group').all,
              }),
              clearGroupsCache(),
            ]);
          },
        }
      );

      onDismiss();
    } catch (error) {
      console.error('Failed to assign routines to groups:', error);
    }
  };

  const handleReset = () => {
    setSelectedGroups([]);
    setSearchKeyword('');
  };

  return (
    <DropDownView
      anchorRef={anchorRef}
      isVisible={isVisible}
      onDismiss={onDismiss}
      width={400}
      height={900}
      backgroundColor={Colors.primaryGrayBackground}
    >
      <View style={styles.container}>
        {/* Search Input - Fixed at top, not scrolled */}
        <View style={styles.searchContainer}>
          <SearchInput
            value={searchKeyword}
            onChangeText={setSearchKeyword}
            backgroundColor={Colors.white}
            placeholder={t('groups.searchGroups')}
            height={44}
          />
        </View>

        <View style={[styles.scrollViewContainer, { height: 400 }]}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {filteredGroups.map((group) => (
              <CheckBoxSelector
                key={group.id}
                option={group.id}
                options={selectedGroups}
                onChange={onGroupChange}
                label={group.name}
              />
            ))}
          </ScrollView>
        </View>

        <View style={styles.footerContainer}>
          <DropdownFooter
            isDirty={isDirty}
            onSubmit={handleSubmit}
            canReset={canReset}
            onReset={handleReset}
          />
        </View>
      </View>
    </DropDownView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'flex-start',
    flexDirection: 'column',
    backgroundColor: Colors.primaryGrayBackground,
    width: '100%',
  },
  scrollViewContainer: {
    flex: 1,
  },
  scrollViewContent: {
    backgroundColor: Colors.primaryGrayBackground,
    flexGrow: 1,
    padding: 12,
  },
  searchContainer: {
    paddingTop: 12,
    paddingLeft: 12,
    paddingRight: 12,
    paddingBottom: 12,
  },
  inputStyle: {
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
    height: 44,
  },
  footerContainer: {
    paddingTop: 8,
  },
});
